import * as React from 'react';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

/**
 * FormField component that wraps a form input with label and optional error message.
 * Provides consistent spacing, layout, and accessibility attributes.
 *
 * @example
 * ```tsx
 * <FormField
 *   label="Email"
 *   htmlFor="email"
 *   error="Email is required"
 *   required
 * >
 *   <Input id="email" type="email" />
 * </FormField>
 * ```
 */

interface FormFieldProps {
  /** Label text for the form field */
  label: string;
  /** ID of the input element this label is for */
  htmlFor: string;
  /** Error message to display below the input */
  error?: string;
  /** Whether this field is required */
  required?: boolean;
  /** Helper text to display below the label */
  helperText?: string;
  /** The input element(s) to render */
  children: React.ReactNode;
  /** Additional className for the wrapper */
  className?: string;
}

export function FormField({
  label,
  htmlFor,
  error,
  required = false,
  helperText,
  children,
  className,
}: FormFieldProps) {
  const errorId = error ? `${htmlFor}-error` : undefined;
  const helperId = helperText ? `${htmlFor}-helper` : undefined;

  // Clone children and add accessibility attributes
  const childrenWithProps = React.Children.map(children, (child) => {
    if (React.isValidElement<React.HTMLAttributes<HTMLElement>>(child)) {
      const newProps: React.HTMLAttributes<HTMLElement> = {
        ...child.props,
        'aria-invalid': error ? true : undefined,
        'aria-describedby': error ? errorId : helperId,
      };
      return React.cloneElement(child, newProps);
    }
    return child;
  });

  return (
    <div className={cn('grid gap-2', className)}>
      <Label htmlFor={htmlFor}>
        {label}
        {required && <span className="ml-1 text-red-600">*</span>}
      </Label>
      {helperText && !error && (
        <p id={helperId} className="text-xs text-content-tertiary">
          {helperText}
        </p>
      )}
      {childrenWithProps}
      {error && (
        <p id={errorId} className="text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
